import {
  IconButton,
  Tooltip,
  Typography,
  Box,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material'
import React, { useState } from 'react'
import moment from 'moment'
import NotesIcon from '@mui/icons-material/Notes'
import PreviewIcon from '@mui/icons-material/Preview'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import {
  ACTIVITY_TYPES,
  END_ACTIVITIES,
  PROCESS_ACTIVITY_STATUSES,
  statusChipColorMap,
} from '../../utility/constants'
import AutoGeneratedDocumentList from './AutoGenerateDocumentList'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { extractDisplayName } from '../../utility/fileUtil'

const timePassed = (startDatetime, endDatetime = new Date()) => {
  let diffMs = new Date(endDatetime).getTime() - new Date(startDatetime).getTime()

  const units = [
    { label: 'year', ms: 1000 * 60 * 60 * 24 * 365 },
    { label: 'month', ms: 1000 * 60 * 60 * 24 * 30 },
    { label: 'day', ms: 1000 * 60 * 60 * 24 },
    { label: 'hrs', ms: 1000 * 60 * 60 },
    { label: 'min', ms: 1000 * 60 },
  ]

  const result = []

  for (const u of units) {
    if (diffMs >= u.ms) {
      const value = Math.floor(diffMs / u.ms)
      diffMs -= value * u.ms
      result.push(`${value} ${u.label}`)
    }
    if (result.length === 2) break
  }

  return result.length ? result.join(', ') : '1 min'
}

// Filter out parallel start/end nodes and reconnect edges
function filterParallelNodes(nodes) {
  const filteredNodes = nodes.filter(
    (n) =>
      n.activity_type !== ACTIVITY_TYPES.PARALLEL_START &&
      n.activity_type !== ACTIVITY_TYPES.PARALLEL_END,
  )

  const nodeMap = new Map(nodes.map((n) => [n.id, n]))

  filteredNodes.forEach((node) => {
    if (Array.isArray(node.from_process_ids)) {
      const newFromIds = []
      node.from_process_ids.forEach((parentId) => {
        const parentNode = nodeMap.get(parentId)
        if (
          parentNode &&
          (parentNode.activity_type === ACTIVITY_TYPES.PARALLEL_START ||
            parentNode.activity_type === ACTIVITY_TYPES.PARALLEL_END)
        ) {
          if (Array.isArray(parentNode.from_process_ids)) {
            newFromIds.push(...parentNode.from_process_ids)
          }
        } else {
          newFromIds.push(parentId)
        }
      })
      node.from_process_ids = [...new Set(newFromIds)]
    }

    if (Array.isArray(node.to_process_ids)) {
      const newToIds = []
      node.to_process_ids.forEach((childId) => {
        const childNode = nodeMap.get(childId)
        if (
          childNode &&
          (childNode.activity_type === ACTIVITY_TYPES.PARALLEL_START ||
            childNode.activity_type === ACTIVITY_TYPES.PARALLEL_END)
        ) {
          if (Array.isArray(childNode.to_process_ids)) {
            newToIds.push(...childNode.to_process_ids)
          }
        } else {
          newToIds.push(childId)
        }
      })
      node.to_process_ids = [...new Set(newToIds)]
    }
  })

  return filteredNodes
}

function getProtectedNodeIds(nodes) {
  const sorted = [...nodes].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))

  const blocks = []
  let currentStart = null

  for (const row of sorted) {
    if (row.activity_type === ACTIVITY_TYPES.PARALLEL_START) {
      currentStart = row
    }

    if (row.activity_type === ACTIVITY_TYPES.PARALLEL_END && currentStart) {
      const isClosed = row.status === 'closed'

      blocks.push({
        startTime: new Date(currentStart.created_at),
        endTime: isClosed ? new Date(row.modified_at) : new Date('2999-12-31'),
        isClosed,
      })

      // Only reset currentStart when the block is closed (matches backend)
      if (isClosed) {
        currentStart = null
      }
    }
  }

  // Unmatched parallel_start → open block, protect everything from startTime onward
  if (currentStart) {
    blocks.push({
      startTime: new Date(currentStart.created_at),
      endTime: new Date('2999-12-31'),
      isClosed: false,
    })
  }

  const protectedIds = nodes
    .filter((n) => {
      const t = new Date(n.created_at)
      return blocks.some((b) => t >= b.startTime && t <= b.endTime)
    })
    .map((n) => n.id)

  return new Set(protectedIds)
}

function generateEdges(nodes) {
  const edges = []
  const nodeIds = new Set(nodes?.map((n) => String(n.id)))
  nodes?.forEach((node) => {
    node?.to_process_ids?.forEach((childId) => {
      if (nodeIds.has(String(childId))) {
        edges.push({ from: node.id, to: childId })
      }
    })
  })
  return edges
}

function computeLayout(nodes, isDMS = false) {
  const edges = generateEdges(nodes)
  const incoming = {}
  const outgoing = {}

  nodes.forEach((n) => {
    incoming[n.id] = []
    outgoing[n.id] = []
  })

  edges.forEach((e) => {
    if (incoming[e.to]) incoming[e.to].push(e.from)
    if (outgoing[e.from]) outgoing[e.from].push(e.to)
  })

  function findRoot(id) {
    let cur = id
    while (incoming[cur]?.length === 1) {
      cur = incoming[cur][0]
    }
    return cur
  }

  const inDegree = {}
  nodes.forEach((n) => (inDegree[n.id] = 0))
  edges.forEach((e) => inDegree[e.to]++)
  const queue = []
  nodes.forEach((n) => {
    if (inDegree[n.id] === 0) queue.push(n.id)
  })

  const order = []
  while (queue.length) {
    const id = queue.shift()
    order.push(id)
    outgoing[id].forEach((to) => {
      inDegree[to]--
      if (inDegree[to] === 0) queue.push(to)
    })
  }

  const level = {}
  function assignLevel(id, lvl) {
    if (level[id] === undefined || lvl > level[id]) {
      level[id] = lvl
      outgoing[id].forEach((to) => assignLevel(to, lvl + 1))
    }
  }

  nodes.forEach((n) => {
    if (incoming[n.id].length === 0) assignLevel(n.id, 0)
  })

  const positions = {}
  const H_STEP = 280
  const V_STEP = isDMS ? 170 : 120

  let rootX = 0
  let rootY = 0

  order.forEach((id) => {
    if (level[id] === 0) {
      positions[id] = {
        x: isDMS ? 40 : rootX,
        y: isDMS ? rootY : 0,
        level: 0,
      }

      if (isDMS) {
        rootY += V_STEP * 3
      } else {
        rootX += H_STEP
      }
    }
  })

  const rootYOffset = {}

  order.forEach((id) => {
    if (level[id] === 0) {
      rootYOffset[id] = positions[id].y
    }
  })

  const levelVals = Object.values(level)
  const maxLvl = levelVals.length ? Math.max(...levelVals) : 0
  for (let lvl = 1; lvl <= maxLvl; lvl++) {
    nodes.forEach((n) => {
      if (level[n.id] === lvl) {
        if (incoming[n.id].length === 1) {
          const parentId = incoming[n.id][0]
          const rootId = findRoot(n.id)
          const baseY = rootYOffset[rootId] ?? 0

          positions[n.id] = {
            x: positions[parentId].x,
            y: baseY + lvl * V_STEP,
            level: lvl,
          }
        } else if (incoming[n.id].length > 1) {
          const avgX =
            incoming[n.id].reduce((sum, pid) => sum + (positions[pid]?.x ?? 0), 0) /
            incoming[n.id].length
          const rootId = findRoot(n.id)
          const baseY = rootYOffset[rootId] ?? 0

          positions[n.id] = {
            x: avgX,
            y: baseY + lvl * V_STEP,
            level: lvl,
          }
        }
      }
    })

    const parentToChildren = {}
    nodes.forEach((n) => {
      if (level[n.id] === lvl && incoming[n.id].length === 1) {
        const parentId = incoming[n.id][0]
        if (!parentToChildren[parentId]) parentToChildren[parentId] = []
        parentToChildren[parentId].push(n.id)
      }
    })
    Object.values(parentToChildren).forEach((children) => {
      if (children.length > 1) {
        const baseX = positions[incoming[children[0]][0]].x
        const totalWidth = (children.length - 1) * H_STEP
        const startX = baseX - totalWidth / 2
        children.forEach((cid, idx) => {
          positions[cid].x = startX + idx * H_STEP
        })
      }
    })
  }

  let fallbackIndex = 0
  nodes.forEach((n) => {
    if (!positions[n.id]) {
      positions[n.id] = {
        x: 100 + fallbackIndex * H_STEP,
        y: 1000,
        level: 999,
      }
      fallbackIndex++
    }
  })

  const grouped = {}
  Object.entries(positions).forEach(([id, pos]) => {
    const lvl = pos.level
    if (!grouped[lvl]) grouped[lvl] = []
    grouped[lvl].push(id)
  })

  return { positions, edges, grouped, incoming, outgoing }
}

const statusLabelMap = {
  closed: 'Closed',
  delegated: 'Delegated',
  active: 'Active',
}

export const PROCESS_STATUSES = {
  [ACTIVITY_TYPES.END_ACCEPTED]: 'Accepted',
  [ACTIVITY_TYPES.END_REJECTED]: 'Rejected',
}

export const PROCESS_STATUSES_COLOR = {
  [ACTIVITY_TYPES.END_ACCEPTED]: '#4caf50',
  [ACTIVITY_TYPES.END_REJECTED]: '#cb4c4a',
}

const TaskStatusChip = ({ status, isAutoClosed, activityType }) => {
  const isEndNode = activityType && PROCESS_STATUSES[activityType]
  const label = isAutoClosed
    ? 'Auto-Closed'
    : isEndNode
      ? PROCESS_STATUSES[activityType]
      : statusLabelMap[status] || 'Active'
  const chipColor = isAutoClosed
    ? '#e6a23c'
    : isEndNode
      ? PROCESS_STATUSES_COLOR[activityType]
      : statusChipColorMap[status] || '#4caf50'

  return (
    <Chip
      label={label}
      sx={{
        fontWeight: 700,
        fontSize: '9px',
        maxHeight: 16,
        minWidth: 60,
        position: 'absolute',
        bottom: 8,
        right: 2,
        border: `1px solid ${chipColor}`,
        color: chipColor,
        backgroundColor: '#fff',
        '& .MuiChip-label': {
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'clip',
          display: 'block',
          paddingLeft:
            activityType === ACTIVITY_TYPES.END_ACCEPTED ||
            activityType === ACTIVITY_TYPES.END_REJECTED
              ? '4px'
              : '6px',
          paddingRight:
            activityType === ACTIVITY_TYPES.END_ACCEPTED ||
            activityType === ACTIVITY_TYPES.END_REJECTED
              ? '4px'
              : '6px',
        },
      }}
      size="small"
      variant="outlined"
    />
  )
}

function renderArrows(
  edges,
  positions,
  nodes,
  isDMS,
  contentWidth,
  contentHeight,
  centerPad = 0,
  xOffset = 0,
) {
  if (!edges || !positions) return null

  const NODE_WIDTH = 260
  const NODE_HEIGHT = isDMS ? 130 : 90

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: contentWidth,
        height: contentHeight,
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      <defs>
        <marker id="arrowhead" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
          <polygon points="0 0, 6 2, 0 4" fill="#9ca3af" />
        </marker>
      </defs>

      {edges.map((edge, index) => {
        const p1 = positions[edge.from]
        const p2 = positions[edge.to]

        if (!p1 || !p2) return null

        const startX = p1.x + NODE_WIDTH / 2 + xOffset + centerPad
        const startY = p1.y + NODE_HEIGHT

        const endX = p2.x + NODE_WIDTH / 2 + xOffset + centerPad
        const endY = p2.y

        let pathData = ''

        if (Math.abs(startX - endX) <= 10) {
          pathData = `M ${startX} ${startY} L ${endX} ${endY}`
        } else {
          const elbowBuffer = 10
          const minXRange = Math.min(startX, endX) - elbowBuffer
          const maxXRange = Math.max(startX, endX) + elbowBuffer
          const minYRange = startY + elbowBuffer
          const maxYRange = endY - elbowBuffer

          const nodeInWay = nodes.some((node) => {
            const pos = positions[node.id]
            if (!pos) return false
            const nodeX = pos.x + NODE_WIDTH / 2 + xOffset
            const nodeY = pos.y
            return (
              node.id !== edge.from &&
              node.id !== edge.to &&
              nodeX >= minXRange &&
              nodeX <= maxXRange &&
              nodeY >= minYRange &&
              nodeY <= maxYRange
            )
          })

          let midY
          if (!nodeInWay && Math.abs(endY - startY) > 100) {
            midY = endY - 18
          } else {
            midY = startY + 12
          }
          pathData = `M ${startX} ${startY} L ${startX} ${midY} L ${endX} ${midY} L ${endX} ${endY}`
        }

        if (Math.abs(endY - startY) < 10 && Math.abs(endX - startX) < 10) return null

        return (
          <path
            key={`arrow-${index}`}
            d={pathData}
            stroke="#9ca3af"
            strokeWidth="1.5"
            fill="none"
            markerEnd="url(#arrowhead)"
          />
        )
      })}
    </svg>
  )
}

const getDmsExtraFields = (node, fieldOfDMS) => {
  if (!node || !fieldOfDMS) return {}

  const tmpl = fieldOfDMS[node.process_template_id]
  if (!tmpl?.fieldMap) return {}

  const fileId = tmpl.fieldMap?.FIle?.[0]
  const noteId = tmpl.fieldMap?.Note?.[0]

  const file = fileId ? node.data?.[`f_${fileId}`] : null
  const note = noteId ? node.data?.[`f_${noteId}`] : null

  return { file, note }
}

export default function Workflow({
  nodes,
  openProcessData = () => {},
  docMap = {},
  fieldOfDMS,
  isDMS,
  onDeleteFromNode,
}) {
  const [docData, setDocData] = useState([])
  const [isDocOpen, setIsDocOpen] = useState(false)
  const [selectedNode, setSelectedNode] = useState(false)

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [nodeToDelete, setNodeToDelete] = useState(null)

  const filteredNodes = filterParallelNodes(nodes)

  const protectedNodeIds = getProtectedNodeIds(nodes)

  // Apex Parallel nodes detection
  const PARALLEL_MARKER_TYPES = [ACTIVITY_TYPES.PARALLEL_START, ACTIVITY_TYPES.PARALLEL_END]

  const nonParallelRawNodes = nodes.filter((n) => !PARALLEL_MARKER_TYPES.includes(n.activity_type))

  // Find the last active node among filtered (visible) nodes.
  const lastActiveNodeId = (() => {
    const sorted = [...filteredNodes].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at),
    )
    for (let i = sorted.length - 1; i >= 0; i--) {
      if (sorted[i].status === PROCESS_ACTIVITY_STATUSES.ACTIVE) {
        return sorted[i].id
      }
    }
    return null
  })()

  // All non-end, non-protected visible nodes (normal candidates for delete).
  const allDeletableNodes = filteredNodes.filter(
    (n) =>
      ![ACTIVITY_TYPES.END_ACCEPTED, ACTIVITY_TYPES.END_REJECTED].includes(n.activity_type) &&
      !protectedNodeIds.has(n.id),
  )

  const isSingleNodeOnly = allDeletableNodes.length === 1 || nonParallelRawNodes.length === 1

  const groupedByRef = isDMS
    ? filteredNodes.reduce((acc, n) => {
        const refKey = n.process_reference_num ?? 'Unknown'
        acc[refKey] ??= []
        acc[refKey].push(n)
        return acc
      }, {})
    : null

  let positions = {}
  let edges = []
  let groupMeta = []

  if (!isDMS) {
    const out = computeLayout(filteredNodes, false)
    positions = out.positions
    edges = out.edges
  } else {
    const GROUP_GAP = 160
    const TOP_PADDING = 40
    let yOffset = TOP_PADDING

    Object.entries(groupedByRef)
      .sort((a, b) => new Date(a[1][0].created_at) - new Date(b[1][0].created_at))
      .forEach(([ref, groupNodes]) => {
        const out = computeLayout(groupNodes, true)
        const prefix = groupNodes[0]?.prefix || ''

        Object.entries(out.positions).forEach(([id, pos]) => {
          positions[id] = {
            ...pos,
            y: pos.y + yOffset,
          }
        })

        edges.push(...out.edges)

        const posVals = Object.values(out.positions)
        const maxY = posVals.length ? Math.max(...posVals.map((p) => p.y)) : 0

        groupMeta.push({
          ref,
          prefix,
          top: yOffset,
          height: maxY + GROUP_GAP / 2,
        })

        yOffset += maxY + GROUP_GAP
      })
  }

  const showDocumentData = async (node) => {
    setIsDocOpen(true)
    setDocData(docMap[node.id])
    setSelectedNode(node)
  }

  const handleDeleteClick = (node) => {
    setNodeToDelete(node)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (nodeToDelete && onDeleteFromNode) {
      onDeleteFromNode(nodeToDelete)
    }
    setDeleteConfirmOpen(false)
    setNodeToDelete(null)
  }

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false)
    setNodeToDelete(null)
  }

  // Used in single-node permanent delete dialog
  const nodeRefLabel = nodeToDelete
    ? `${nodeToDelete.prefix || ''}-${nodeToDelete.process_reference_num || ''}`.replace(/^-/, '')
    : ''

  const allPosValues = Object.values(positions)
  const NODE_WIDTH = 260
  const NODE_HEIGHT = isDMS ? 130 : 90

  const contentMinX = allPosValues.length ? Math.min(...allPosValues.map((p) => p.x)) : 0
  const contentMaxX = allPosValues.length ? Math.max(...allPosValues.map((p) => p.x)) : 0
  const contentMaxY = allPosValues.length ? Math.max(...allPosValues.map((p) => p.y)) : 0

  const xOffset = contentMinX < 0 ? Math.abs(contentMinX) : 0
  const contentWidth = contentMaxX - contentMinX + NODE_WIDTH + 100
  const contentHeight = contentMaxY + NODE_HEIGHT + 100
  const centerPad = 50

  return (
    <>
      <div
        style={{
          padding: '12px 0px',
          overflowY: 'auto',
          overflowX: 'auto',
          height: '100vh',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: contentWidth,
            height: contentHeight,
            flexShrink: 0,
            margin: '0px auto',
          }}
        >
          {isDMS &&
            groupMeta.map((g) => (
              <div
                key={`${g.prefix}-${g.ref}`}
                style={{
                  position: 'absolute',
                  left: '90px',
                  top: g.top - 24,
                  fontWeight: 700,
                  fontSize: 13,
                  background: '#f3f4f8',
                  padding: '4px 10px',
                  borderRadius: 6,
                  border: '1px solid #e0e3ef',
                  zIndex: 1,
                  color: '#333',
                }}
              >
                {g.prefix}-{g.ref}
              </div>
            ))}
          {renderArrows(
            edges,
            positions,
            filteredNodes,
            isDMS,
            contentWidth,
            contentHeight,
            centerPad,
            xOffset,
          )}

          {filteredNodes.map((node) => {
            const pos = positions[node.id]
            if (!pos) return null
            const hasDocuments = docMap[node.id]?.length > 0

            let backgroundColor = '#fff'
            let borderLeftColor = '#4caf50'

            if (node.status === PROCESS_ACTIVITY_STATUSES.CLOSED) {
              if (node.activity_type === 'end-accepted') {
                borderLeftColor = '#4caf50'
              } else if (node.activity_type === 'end-rejected') {
                borderLeftColor = '#cb4c4a'
              } else {
                borderLeftColor = statusChipColorMap.closed // fallback (if needed)
              }
            } else if (node.status === PROCESS_ACTIVITY_STATUSES.DELEGATED) {
              borderLeftColor = statusChipColorMap.delegated
            } else if (node.status === PROCESS_ACTIVITY_STATUSES.ACTIVE) {
              borderLeftColor = statusChipColorMap.active
            }

            const isEndNode = END_ACTIVITIES.includes(node.activity_type)

            const isParallelProtected = protectedNodeIds.has(node.id)
            const isLastActiveNode = node.id === lastActiveNodeId

            const isOnlyRealNode =
              nonParallelRawNodes.length === 1 && nonParallelRawNodes[0].id === node.id

            const isDelegationNode = String(node.id).includes('_delegated_')

            const showDeleteBtn =
              !!onDeleteFromNode &&
              !isEndNode &&
              !isDelegationNode &&
              (!isParallelProtected || isOnlyRealNode) &&
              (!isLastActiveNode || isSingleNodeOnly)

            return (
              <div
                key={node.id}
                style={{
                  position: 'absolute',
                  top: pos.y,
                  padding: '4px',
                  left: pos.x + xOffset + centerPad,
                  borderRadius: '0px',
                  width: '240px',
                  minHeight: '60px',
                  minWidth: '200px',
                  maxHeight: isDMS ? '140px' : '100px',
                  background: backgroundColor,
                  border: '1px solid #e5e7ef',
                  borderLeft: `3px solid ${borderLeftColor}`,
                  zIndex: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'visible',
                  boxSizing: 'border-box',
                }}
              >
                {showDeleteBtn && (
                  <Tooltip
                    title={
                      isSingleNodeOnly
                        ? 'Delete this task permanently'
                        : 'Re-assign here and delete successive nodes'
                    }
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(node)}
                      sx={{
                        position: 'absolute',
                        top: -14,
                        right: -14,
                        zIndex: 10,
                        padding: '1px',
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        '&:hover': {
                          backgroundColor: '#ffeaea',
                          borderColor: '#f44336',
                          '& svg': { color: '#f44336' },
                        },
                      }}
                    >
                      <RemoveCircleOutlineIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
                    </IconButton>
                  </Tooltip>
                )}

                <Box
                  sx={{
                    p: 0.5,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 'auto',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: isDMS ? 1.5 : 0.5,
                      minHeight: 0,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: '12px',
                        color: '#333',
                        lineHeight: 1.2,
                        flex: 1,
                        mr: 0.5,
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                      }}
                    >
                      {node.activity_template_name}
                      {!isEndNode && node.version && `(v${node.version})`}
                    </Typography>

                    {/* <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                      <Tooltip title={node.overall_comments} placement="top">
                        <IconButton size="small" disabled={node.is_restricted || isEndNode}>
                          <NotesIcon style={{ fontSize: 12 }} />
                        </IconButton>
                      </Tooltip>

                      {hasDocuments && (
                        <Tooltip
                          title={`${docMap[node.id].length} auto-generated document(s)`}
                          placement="top"
                        >
                          <IconButton
                            size="small"
                            onClick={() => showDocumentData(node)}
                            sx={{ p: 0.25, minWidth: 'auto' }}
                          >
                            <AttachFileIcon
                              sx={{
                                fontSize: 14,
                                color: 'rgb(0 0 0 / 54%)',
                                transform: 'rotate(45deg)',
                                flexShrink: 0,
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="View Process Data" placement="top">
                        <IconButton
                          size="small"
                          sx={{
                            '&:hover': { bgcolor: '#f5f5f5' },
                            p: 0.25,
                            minWidth: 'auto',
                          }}
                          onClick={() => {
                            if (
                              node.status !== PROCESS_ACTIVITY_STATUSES.DELEGATED &&
                              !node.is_parallel_activity &&
                              !node.is_restricted &&
                              !isEndNode
                            ) {
                              openProcessData(node)
                            }
                          }}
                          disabled={
                            node.status === PROCESS_ACTIVITY_STATUSES.DELEGATED ||
                            node.is_parallel_activity ||
                            node.is_restricted ||
                            isEndNode
                          }
                        >
                          <PreviewIcon style={{ fontSize: 12 }} />
                        </IconButton>
                      </Tooltip>
                    </Box> */}

                    <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                      {!isEndNode && (
                        <Tooltip title={node.overall_comments} placement="top">
                          <IconButton size="small" disabled={node.is_restricted}>
                            <NotesIcon style={{ fontSize: 12 }} />
                          </IconButton>
                        </Tooltip>
                      )}

                      {hasDocuments && (
                        <Tooltip
                          title={`${docMap[node.id].length} auto-generated document(s)`}
                          placement="top"
                        >
                          <IconButton
                            size="small"
                            onClick={() => showDocumentData(node)}
                            sx={{ p: 0.25, minWidth: 'auto' }}
                          >
                            <AttachFileIcon
                              sx={{
                                fontSize: 14,
                                color: 'rgb(0 0 0 / 54%)',
                                transform: 'rotate(45deg)',
                                flexShrink: 0,
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      )}

                      {!isEndNode && (
                        <Tooltip title="View Process Data" placement="top">
                          <IconButton
                            size="small"
                            sx={{
                              '&:hover': { bgcolor: '#f5f5f5' },
                              p: 0.25,
                              minWidth: 'auto',
                            }}
                            onClick={() => {
                              if (
                                node.status !== PROCESS_ACTIVITY_STATUSES.DELEGATED &&
                                !node.is_parallel_activity &&
                                !node.is_restricted
                              ) {
                                openProcessData(node)
                              }
                            }}
                            disabled={
                              node.status === PROCESS_ACTIVITY_STATUSES.DELEGATED ||
                              node.is_parallel_activity ||
                              node.is_restricted
                            }
                          >
                            <PreviewIcon style={{ fontSize: 12 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ mb: 0.5 }}>
                    <TaskStatusChip
                      status={node.status}
                      isAutoClosed={node.metadata?.is_auto_closed}
                      activityType={node.activity_type}
                    />
                  </Box>

                  {!isEndNode && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        mb: 0.5,
                        minHeight: 0,
                        width: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#4b4f60',
                            fontSize: '10px',
                            fontWeight: 400,
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                        >
                          {node.assignee}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: '#999',
                            fontSize: '10px',
                            fontWeight: 500,
                            lineHeight: 1.2,
                            ml: 0,
                          }}
                        >
                          {node.status == 'active'
                            ? `(${timePassed(node.created_at)})`
                            : `(${timePassed(node.created_at, node.modified_at)})`}
                        </Typography>
                      </Box>
                      {/* Right side — Auto-approved badge */}
                      {node.overall_comments == 'Auto-approved' && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '10px',
                            fontWeight: 500,
                            color: '#0F6E56',
                            backgroundColor: '#E1F5EE',
                            borderRadius: '4px',
                            px: '5px',
                            py: '1px',
                            lineHeight: 1.4,
                            whiteSpace: 'nowrap',
                            alignSelf: 'center',
                          }}
                        >
                          Auto-approved
                        </Typography>
                      )}
                    </Box>
                  )}

                  {isDMS &&
                    (() => {
                      const { file, note } = getDmsExtraFields(node, fieldOfDMS)
                      if (!file && !note) return null
                      return (
                        <Box sx={{ mb: 0.5 }}>
                          {file && (
                            <Typography
                              sx={{
                                fontSize: '10px',
                                color: '#374151',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              File: {extractDisplayName(file)}
                            </Typography>
                          )}
                          {note && (
                            <Typography
                              sx={{
                                fontSize: '10px',
                                color: '#6b7280',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Note: {note}
                            </Typography>
                          )}
                        </Box>
                      )
                    })()}

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: hasDocuments ? 0.5 : 0,
                      minHeight: 0,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                      <CalendarMonthIcon sx={{ fontSize: '12px', color: '#656d92' }} />
                      <Typography
                        variant="caption"
                        sx={{ color: '#50566d', fontSize: '10px', fontWeight: 400 }}
                      >
                        {moment(node.created_at).format('DD MMM YYYY')}
                      </Typography>
                    </Box>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ height: '12px', alignSelf: 'center' }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                      <AccessTimeIcon sx={{ fontSize: '12px', color: '#656d92' }} />
                      <Typography
                        variant="caption"
                        sx={{ color: '#50566d', fontSize: '10px', fontWeight: 400 }}
                      >
                        {moment(node.created_at).format('hh:mm A')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </div>
            )
          })}
        </div>
      </div>

      {isDocOpen && (
        <AutoGeneratedDocumentList
          isDocOpen={isDocOpen}
          setIsDocOpen={setIsDocOpen}
          title={'Auto Generated Document(s)'}
          docData={docData}
          selectedNode={selectedNode}
        />
      )}

      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>Confirm Action</DialogTitle>

        <DialogContent>
          {isSingleNodeOnly ? (
            <Typography variant="body2">
              Are you sure you want to delete{' '}
              <Box component="span" sx={{ fontWeight: 600 }}>
                {nodeRefLabel}
              </Box>
              ?
              <br />
              <Box component="span" sx={{ fontWeight: 500 }}>
                This will permanently remove the entire process record.
              </Box>
            </Typography>
          ) : (
            <Typography variant="body2">
              Are you sure you want to rewind to{' '}
              <Box component="span" sx={{ fontWeight: 600 }}>
                {nodeToDelete?.activity_template_name}
                {nodeToDelete?.version ? ` (v${nodeToDelete.version})` : ''}
              </Box>
              ?
              <br />
              <Box component="span" sx={{ fontWeight: 500 }}>
                All process data created after this point will be permanently removed.
              </Box>
            </Typography>
          )}

          <Typography variant="body2" sx={{ mt: 1 }}>
            <b>*</b> This action cannot be undone
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDeleteCancel} size="small">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" size="small">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
